//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
    GetStudentResponseDissertative,
    GetStudentResponseDissertativeForStudent,
    GetStudentResultDissertative,
} from "./types/types";

/**
 * @brief Este objeto contém as funções da API para gerenciar respostas dissertativas dos alunos.
 */
export const studentResponseDissertativesAPI = {
  /**
   * @async
   * @function getAll
   * @brief Busca todas as respostas dissertativas dos alunos com pesquisa e paginação opcionais.
   *
   * @param {string} search - Termo de pesquisa para filtrar respostas (opcional).
   * @param {number} page - Número da página para paginação (padrão é 1).
   * @param {string} token - Token de autorização para a requisição.
   * @param {number} perPage - Número de itens por página (padrão é 10).
   * @returns {Promise<GetStudentResponseDissertative>} Uma promise que resolve para um objeto GetStudentResponseDissertative contendo respostas e dados de paginação.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetStudentResponseDissertative> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/student-responses-dissertatives?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          responses: data.student_responses_dissertatives || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getAllByStudent
   * @brief Busca alunos que responderam a uma aplicação específica.
   *
   * @param {string} applicationId - O ID da aplicação.
   * @param {string} token - Token de autorização para a requisição.
   * @param {number} page - Número da página para paginação (padrão é 1).
   * @param {number} perPage - Número de itens por página (padrão é 10).
   * @param {string} search - (Opcional) Termo de pesquisa para filtrar por nome ou email.
   * @returns {Promise<GetStudentResponseDissertativeForStudent>} Uma promise que resolve para um objeto GetStudentResponseDissertativeForStudent contendo estudantes e dados de paginação.
   */
  getAllByStudent: async function (
    applicationId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    search?: string
  ): Promise<GetStudentResponseDissertativeForStudent> {
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
    const url = `${API_URI}/admin/private/student-responses-dissertatives/${applicationId}/students?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          students: data.students || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getAllStudentResponsesByStudent
   * @brief Busca todas as respostas dissertativas para um aluno específico em uma aplicação.
   * @param {string} applicationId - O ID da aplicação.
   * @param {string} studentId - O ID do aluno.
   * @param {string} token - Token de autorização para a requisição.
   * @param {number} page - Número da página para paginação (padrão é 1).
   * @param {number} perPage - Número de itens por página (padrão é 10).
   * @param {string} status - (Opcional) Status para filtrar as respostas.
   * @param {string} search - (Opcional) Termo de pesquisa para filtrar respostas.
   * @returns {Promise<GetStudentResponseDissertative>} Uma promise que resolve para um objeto GetStudentResponseDissertative contendo respostas e dados de paginação.
   */
  getAllStudentResponsesByStudent: async function (
    applicationId: string,
    studentId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    status?: string,
    search?: string
  ): Promise<GetStudentResponseDissertative> {
    let url = `${API_URI}/admin/private/student-responses-dissertatives/${applicationId}/students/${studentId}/results?page=${page}&perPage=${perPage}`;

    if (status) {
      url += `&status=${status}`;
    }

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;


        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          responses: data.responses || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getResults
   * @brief Busca os resultados agregados de cada aluno para uma aplicação específica, permitindo filtragem por nome do aluno.
   *
   * @param {string} applicationId - O ID da aplicação.
   * @param {string} token - Token de autorização.
   * @param {number} page - Página atual (padrão 1).
   * @param {number} perPage - Itens por página (padrão 10).
   * @param {string} search - Termo de busca (opcional).
   * @returns {Promise<GetStudentResultDissertative>} Um objeto com os resultados e dados de paginação.
   */
  getResults: async function (
    applicationId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    search?: string
  ): Promise<GetStudentResultDissertative> {
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
    const url = `${API_URI}/admin/private/student-responses-dissertatives/${applicationId}/results?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          results: data.results || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getStudentResultsByStudent
   * @brief Busca os resultados de um aluno específico em uma aplicação.
   *
   * @param {string} applicationId - O ID da aplicação.
   * @param {string} studentId - O ID do aluno.
   * @param {string} token - Token de autorização para a requisição.
   * @returns {Promise<any>} Uma promise que resolve para os resultados do aluno.
   */
  getStudentResultsByStudent: async function (
    applicationId: string,
    studentId: string,
    token: string
  ): Promise<any> {
    const url = `${API_URI}/admin/private/student-responses-dissertatives/${applicationId}/students/${studentId}/results`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Erro ao buscar resultados do aluno:", error);
        throw error;
      });
  },

  /**
   * @async
   * @function updateStudentResponseDissertative
   * @brief Atualiza uma resposta dissertativa de um aluno.
   *
   * @param {string} id - O ID da resposta dissertativa a ser atualizada.
   * @param {object} updateData - Os dados a serem atualizados.
   * @param {string} token - O token de autorização para a requisição.
   * @returns {Promise<void>}
   */
  updateStudentResponseDissertative: async function (
    id: string,
    updateData: { is_wrong: boolean | null },
    token: string
  ): Promise<void> {
    const url = `${API_URI}/admin/private/student-responses-dissertatives/${id}`;

    return api
      .request({
        url,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          is_wrong: updateData.is_wrong,
        },
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error("Erro ao atualizar resposta dissertativa:", error);
        throw error;
      });
  },

  /**
   * @async
   * @function getById
   * @brief Busca uma resposta dissertativa específica pelo seu ID.
   *
   * @param {string} id - O ID da resposta dissertativa.
   * @param {string} token - Token de autorização para a requisição.
   * @returns {Promise<any>} Uma promise que resolve para os detalhes da resposta dissertativa.
   */
  getById: async function (id: string, token: string): Promise<any> {
    const url = `${API_URI}/admin/private/student-responses-dissertatives/${id}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data?.data?.student_response_dissertative;
      })
      .catch((error) => {
        console.error("Erro ao buscar resposta dissertativa por ID:", error);
        throw error;
      });
  },
};